import React, { memo } from 'react';
import { ArrowRight, Check, ShieldCheck, Heart, BookOpen, Star } from 'lucide-react';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialCard } from '../components/TestimonialCard';
import { BonusSection } from '../components/BonusSection';
export function SalesPage() {
  const scrollToPurchase = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#FFFDF9] font-sans text-gray-800 selection:bg-amber-100 selection:text-amber-900" data-id="element-35">
      <PurchaseNotification data-id="element-36" />

      {/* Navigation Bar - Minimal */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-amber-100 py-4 px-6" data-id="element-37">
        <div className="max-w-4xl mx-auto flex justify-between items-center" data-id="element-38">
          <div className="font-bold text-amber-600 text-lg flex items-center gap-2" data-id="element-39">
            <BookOpen className="w-5 h-5" data-id="element-40" />
            <span data-id="element-41">Matemática sin Lágrimas</span>
          </div>
          <button onClick={scrollToPurchase} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors shadow-sm hover:shadow-md" data-id="element-42">
            Acceder Ahora
          </button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-20" data-id="element-43">
        {/* Hero Section / Hook */}
        <section className="mb-16 animate-in fade-in slide-in-from-bottom-4 duration-700" data-id="element-44">
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-8 font-serif" data-id="element-45">
            Hay un momento del día que muchas madres temen más que cualquier
            reunión, cualquier pendiente o cualquier problema del trabajo.
          </h1>

          <div className="prose prose-lg prose-amber max-w-none text-gray-700" data-id="element-46">
            <p className="font-medium text-xl text-amber-800/80 italic border-l-4 border-amber-300 pl-4 py-2 bg-amber-50 rounded-r-lg mb-8" data-id="element-47">
              No es a la mañana. No es a la noche.
              <br data-id="element-48" />
              Es cuando llega la tarea.
            </p>

            <p data-id="element-49">
              Te sentás al lado de tu hijo con la mejor intención del mundo.
              Querés ayudar. Querés que le vaya bien. Querés que aprenda.
            </p>
            <p data-id="element-50">Y en pocos minutos… todo se desarma.</p>
            <p data-id="element-51">
              Él no entiende. Vos explicás. Volvés a explicar. Lo decís "de otra
              forma". Te escucha, pero no responde. Se frustra. Se bloquea.
              Llora. O se enoja.
            </p>
            <p data-id="element-52">Y ahí aparece eso que nunca quisiste que aparezca:</p>
            <p className="font-semibold text-gray-900" data-id="element-53">
              Tu tono cambia. Te impacientás. Suspirás. Te sentís torpe. Te
              sentís mala madre.
            </p>
            <p data-id="element-54">Y la frase que más duele pensar cruza la cabeza:</p>
            <p className="text-xl font-serif text-center my-8 text-gray-900" data-id="element-55">
              "¿Cómo puede ser que algo tan básico termine siempre en pelea?"
            </p>
          </div>
        </section>

        {/* Product Image Showcase */}
        <section className="my-16 relative group cursor-pointer" onClick={scrollToPurchase} data-id="element-56">
          <div className="absolute -inset-4 bg-gradient-to-r from-amber-100 to-blue-100 rounded-3xl opacity-50 blur-xl group-hover:opacity-70 transition-opacity duration-500" data-id="element-57"></div>
          <img src="https://cdn.magicpatterns.com/uploads/wuyBrC815YUtePVdT5eRwe/ChatGPT_Image_15_ene_2026,_10_54_55_p.m..png" alt="Matemática sin Lágrimas Libro Digital" className="relative w-full rounded-2xl shadow-2xl transform group-hover:scale-[1.01] transition-transform duration-500" data-id="element-58" />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur px-6 py-2 rounded-full shadow-lg text-sm font-bold text-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-59">
            Ver Oferta Especial
          </div>
        </section>

        {/* Empathy & Validation */}
        <section className="mb-16 prose prose-lg prose-amber max-w-none text-gray-700" data-id="element-60">
          <p data-id="element-61">
            Si esto te resulta familiar, frená un segundo y leé esto con
            atención:
          </p>

          <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 my-8 shadow-sm" data-id="element-62">
            <ul className="space-y-3 list-none pl-0 my-0" data-id="element-63">
              <li className="flex items-center gap-3" data-id="element-64">
                <Check className="w-5 h-5 text-blue-500 flex-shrink-0" data-id="element-65" />
                <span className="font-medium" data-id="element-66">No sos mala madre</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-67">
                <Check className="w-5 h-5 text-blue-500 flex-shrink-0" data-id="element-68" />
                <span className="font-medium" data-id="element-69">No sos impaciente</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-70">
                <Check className="w-5 h-5 text-blue-500 flex-shrink-0" data-id="element-71" />
                <span className="font-medium" data-id="element-72">No fracasaste como mamá</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-73">
                <Check className="w-5 h-5 text-blue-500 flex-shrink-0" data-id="element-74" />
                <span className="font-medium" data-id="element-75">
                  Y no necesitás saber más matemática
                </span>
              </li>
            </ul>
          </div>

          <p data-id="element-76">
            Lo que te pasa tiene una explicación. Y, lo más importante: tiene
            solución.
          </p>
          <p data-id="element-77">Porque el verdadero problema no es la matemática.</p>
          <p className="font-bold text-amber-700" data-id="element-78">
            El verdadero problema es que nadie te enseñó cómo acompañar el
            aprendizaje sin entrar en conflicto.
          </p>
          <p data-id="element-79">
            A vos te enseñaron matemática de memoria. A repetir. A resolver como
            te dijeron. A "hacerlo así porque sí".
          </p>
          <p data-id="element-80">
            Y ahora te encontrás intentando ayudar a tu hijo con un sistema que
            ya no funciona, con contenidos que cambiaron, métodos nuevos… y una
            carga emocional enorme arriba de la mesa.
          </p>

          <div className="my-12 text-center" data-id="element-81">
            <button onClick={scrollToPurchase} className="bg-amber-500 hover:bg-amber-600 text-white text-lg font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center gap-2 mx-auto" data-id="element-82">
              Quiero Terminar con las Peleas <ArrowRight className="w-5 h-5" data-id="element-83" />
            </button>
          </div>

          <p data-id="element-84">
            No estás sola en esto. Miles de madres viven la misma escena todos
            los días:
          </p>
          <ul className="list-disc pl-6 space-y-2 marker:text-amber-500" data-id="element-85">
            <li data-id="element-86">Hijos que dicen "no entiendo nada"</li>
            <li data-id="element-87">Madres que sienten que explican bien pero no logran llegar</li>
            <li data-id="element-88">Tareas que duran horas</li>
            <li data-id="element-89">Peleas que dejan culpa</li>
            <li data-id="element-90">Niños que empiezan a odiar la materia</li>
            <li data-id="element-91">Madres que terminan agotadas emocionalmente</li>
          </ul>

          <p data-id="element-92">
            Y lo peor no es la tarea en sí. Lo peor es lo que queda después.
          </p>
          <p data-id="element-93">
            El silencio incómodo. La sensación de haber fallado. El nudo en el
            pecho. La culpa por haber perdido la paciencia. La duda constante:
          </p>
          <p className="italic text-center text-xl text-gray-600 my-6" data-id="element-94">
            "¿Le estaré haciendo mal sin darme cuenta?"
          </p>
        </section>

        {/* The Solution / Vision */}
        <section className="mb-16 bg-gradient-to-b from-white to-amber-50 p-8 rounded-3xl border border-amber-100 shadow-sm" data-id="element-95">
          <div className="prose prose-lg prose-amber max-w-none text-gray-700" data-id="element-96">
            <h3 className="text-2xl font-bold text-amber-800 mt-0" data-id="element-97">
              Imaginate, por un momento, que la escena fuera distinta.
            </h3>

            <p data-id="element-98">Que tu hijo te mire y diga: "Ah, ahora sí entiendo".</p>
            <p data-id="element-99">
              Que la tarea se haga en menos tiempo. Sin llanto. Sin gritos. Sin
              tensión.
            </p>
            <p data-id="element-100">
              Que vos te sientas tranquila. Segura. Acompañando, no empujando.
            </p>
            <p data-id="element-101">
              Que la matemática deje de ser un campo de batalla y pase a ser
              solo una parte más del día.
            </p>

            <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-amber-500 my-8" data-id="element-102">
              <p className="text-xl font-bold text-gray-900 m-0" data-id="element-103">
                Eso es exactamente lo que hace posible{' '}
                <span className="text-amber-600" data-id="element-104">Matemática sin Lágrimas</span>.
              </p>
            </div>

            <p data-id="element-105">
              Y no, no es un libro de fórmulas. No es un curso pesado. No es una
              guía para que te conviertas en maestra.
            </p>
            <p data-id="element-106">Es algo mucho más simple… y mucho más poderoso.</p>
            <p data-id="element-107">
              Es un método pensado para madres reales, con hijos reales, con
              tiempos reales y con emociones reales.
            </p>
            <p data-id="element-108">
              Un sistema que te muestra cómo ayudar a tu hijo con la tarea sin
              peleas, sin frustración y sin saber más matemática que él.
            </p>
          </div>
        </section>

        {/* The Core Insight */}
        <section className="mb-16 prose prose-lg prose-amber max-w-none text-gray-700" data-id="element-109">
          <h3 className="text-2xl font-bold text-gray-900" data-id="element-110">
            Porque acá está la verdad que nadie dice:
          </h3>

          <div className="grid gap-4 my-6" data-id="element-111">
            <div className="flex items-start gap-4 p-4 bg-red-50 rounded-lg border border-red-100" data-id="element-112">
              <div className="bg-red-100 p-2 rounded-full mt-1" data-id="element-113">
                <span className="text-red-600 font-bold" data-id="element-114">1</span>
              </div>
              <p className="m-0" data-id="element-115">Tu hijo no se bloquea porque no puede.</p>
            </div>
            <div className="flex items-start gap-4 p-4 bg-red-50 rounded-lg border border-red-100" data-id="element-116">
              <div className="bg-red-100 p-2 rounded-full mt-1" data-id="element-117">
                <span className="text-red-600 font-bold" data-id="element-118">2</span>
              </div>
              <p className="m-0" data-id="element-119">
                Se bloquea porque se siente presionado, evaluado o inseguro.
              </p>
            </div>
            <div className="flex items-start gap-4 p-4 bg-red-50 rounded-lg border border-red-100" data-id="element-120">
              <div className="bg-red-100 p-2 rounded-full mt-1" data-id="element-121">
                <span className="text-red-600 font-bold" data-id="element-122">3</span>
              </div>
              <p className="m-0" data-id="element-123">
                Y sin darse cuenta, los adultos solemos activar ese bloqueo.
              </p>
            </div>
          </div>

          <p data-id="element-124">
            No por maldad. No por ignorancia. Sino porque nadie nos enseñó otra
            forma.
          </p>
          <p data-id="element-125">
            La mayoría de las soluciones que existen atacan el problema
            equivocado. Te dicen: "Que practique más", "Que memorice", "Que
            repita ejercicios", "Que vaya a apoyo escolar".
          </p>
          <p data-id="element-126">
            ¿Y qué pasa? Practica, pero sigue sin entender. Memoriza, pero se
            frustra igual. Va a apoyo… y en casa la pelea continúa.
          </p>

          <div className="my-12 text-center" data-id="element-127">
            <button onClick={scrollToPurchase} className="bg-amber-500 hover:bg-amber-600 text-white text-lg font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center gap-2 mx-auto" data-id="element-128">
              Sí, Quiero Ayudar Sin Discutir{' '}
              <Heart className="w-5 h-5 fill-white" data-id="element-129" />
            </button>
          </div>
        </section>

        {/* The Method Explanation */}
        <section className="mb-16" data-id="element-130">
          <div className="bg-amber-900 text-white p-8 rounded-3xl shadow-xl relative overflow-hidden" data-id="element-131">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-800 rounded-full mix-blend-multiply filter blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2" data-id="element-132"></div>

            <h3 className="text-2xl font-bold mb-6 relative z-10" data-id="element-133">
              Matemática sin Lágrimas parte de una idea distinta:
            </h3>

            <div className="space-y-6 relative z-10 text-amber-50" data-id="element-134">
              <p className="text-lg" data-id="element-135">
                👉 El aprendizaje no falla por falta de contenido.
                <br data-id="element-136" />
                👉 Falla por cómo se acompaña emocionalmente.
              </p>

              <p data-id="element-137">
                Cuando un niño se siente seguro, escuchado y comprendido, su
                cerebro aprende. Cuando se siente presionado, comparado o
                apurado, se cierra.
              </p>

              <div className="bg-white/10 p-6 rounded-xl border border-white/20 mt-6" data-id="element-138">
                <p className="font-bold mb-4 text-white" data-id="element-139">
                  Este método te enseña a:
                </p>
                <ul className="space-y-3" data-id="element-140">
                  {['Explicar sin imponer', 'Guiar sin resolver por él', 'Detectar dónde se traba sin frustrarlo', 'Hacer preguntas que destraban', 'Acompañar sin discutir', 'Cerrar la tarea sin lágrimas'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-141">
                      <Check className="w-5 h-5 text-amber-300 flex-shrink-0" data-id="element-142" />
                      <span data-id="element-143">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Metaphors Section */}
        <section className="mb-16 prose prose-lg prose-amber max-w-none text-gray-700" data-id="element-144">
          <h3 className="text-2xl font-bold text-gray-900" data-id="element-145">Pensalo así:</h3>

          <div className="grid md:grid-cols-2 gap-6 not-prose my-8" data-id="element-146">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-147">
              <h4 className="font-bold text-amber-600 mb-2" data-id="element-148">
                La Puerta Cerrada
              </h4>
              <p className="text-gray-600 text-sm" data-id="element-149">
                Ayudar con la tarea es como empujar una puerta. Muchas madres
                empujan fuerte… y la puerta no se mueve. No porque no tengan
                fuerza. Sino porque está cerrada con llave.{' '}
                <span className="font-semibold" data-id="element-150">
                  Matemática sin Lágrimas te muestra dónde está la llave.
                </span>
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-151">
              <h4 className="font-bold text-amber-600 mb-2" data-id="element-152">
                El Auto y el Miedo
              </h4>
              <p className="text-gray-600 text-sm" data-id="element-153">
                No estás intentando arreglar un auto sin saber mecánica. Estás
                intentando enseñarle a manejar a alguien que tiene miedo de
                chocar. Si solo gritás "acelerá", el miedo aumenta. Si le
                mostrás cómo, con calma, el miedo baja.
              </p>
            </div>
          </div>

          <p className="font-medium text-xl text-center text-amber-800" data-id="element-154">
            El resultado es simple, pero enorme:
          </p>
          <div className="flex flex-wrap justify-center gap-3 my-6 not-prose" data-id="element-155">
            {['Menos discusiones', 'Más comprensión', 'Más confianza', 'Mejor vínculo', 'Hijo capaz'].map(tag => <span key={tag} className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-medium text-sm" data-id="element-156">
                {tag}
              </span>)}
          </div>
        </section>

        {/* Origin Story */}
        <section className="mb-16 prose prose-lg prose-amber max-w-none text-gray-700 bg-gray-50 p-8 rounded-3xl" data-id="element-157">
          <h3 className="text-2xl font-bold text-gray-900 mt-0" data-id="element-158">
            Ahora dejame contarte cómo nació esto.
          </h3>
          <p data-id="element-159">
            Matemática sin Lágrimas no nació en un escritorio. Nació en la misma
            situación en la que probablemente estás vos ahora.
          </p>
          <p data-id="element-160">
            De tardes eternas de tarea. De frustración. De lágrimas. De culpa.
            De la pregunta silenciosa: "¿Por qué algo tan básico nos está
            separando?"
          </p>
          <p data-id="element-161">
            Fue creado después de ver que el problema no era la matemática, sino
            la forma en la que los adultos acompañamos el proceso.
          </p>
          <p data-id="element-162">
            Probando. Observando. Cambiando preguntas. Cambiando el tono.
            Cambiando el enfoque. Hasta que algo pasó.
          </p>
          <p className="font-bold text-amber-700" data-id="element-163">
            Las peleas bajaron. El tiempo de tarea se acortó. La comprensión
            apareció. Y lo más importante: el vínculo se cuidó.
          </p>
          <p data-id="element-164">
            Eso es lo que hoy tenés en tus manos. No teoría. No discurso. No
            promesas vacías. Un método claro, simple y humano.
          </p>
        </section>

        {/* Testimonials */}
        <section className="mb-16" data-id="element-165">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8" data-id="element-166">
            Lo que dicen otras mamás
          </h3>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-167">
            <TestimonialCard name="Ana Sofía" age={38} quote="Antes la hora de la tarea era un infierno. Gritos, llantos, portazos. Con este método entendí que yo estaba bloqueando a mi hijo sin querer. Ayer terminamos la tarea en 20 minutos y riéndonos. No lo puedo creer." data-id="element-168" />
            <TestimonialCard name="Patricia" age={35} quote="Siempre fui mala para matemática y eso me daba inseguridad al explicar. Este libro me dio la confianza para acompañar a mi hija sin tener que ser profesora. Ella está más segura y yo más tranquila." data-id="element-169" />
            <TestimonialCard name="Gabriela" age={42} quote="Lo compré pensando que era otro libro de ejercicios, pero es mucho más. Es una guía emocional. Mi hijo pasó de decir 'soy burro' a decir 'ah, ya entendí'. Eso no tiene precio." data-id="element-170" />
            <TestimonialCard name="Lucía" age={34} quote="La 'Guía de Emergencia' me salvó la vida literal. Estábamos en medio de una crisis, apliqué los pasos y en 5 minutos estábamos calmados retomando. Increíble." data-id="element-171" />
          </div>
          <div className="mt-6 md:w-1/2 mx-auto" data-id="element-172">
            <TestimonialCard name="Valentina" age={40} quote="No solo mejoró la matemática, mejoró nuestra relación. Dejamos de ser enemigas a la hora de estudiar. Gracias por esto." data-id="element-173" />
          </div>
        </section>

        {/* Who is this for */}
        <section className="mb-16 grid md:grid-cols-2 gap-8" data-id="element-174">
          <div className="bg-green-50 p-6 rounded-2xl border border-green-100" data-id="element-175">
            <h4 className="font-bold text-green-800 text-lg mb-4 flex items-center gap-2" data-id="element-176">
              <Check className="w-5 h-5" data-id="element-177" /> Es para vos si:
            </h4>
            <ul className="space-y-3" data-id="element-178">
              {['Te sentís agotada al momento de la tarea', 'Querés ayudar sin pelear', 'No sos experta en matemática', 'Te importa el aprendizaje y el vínculo', 'Querés que tu hijo deje de sufrir'].map((item, i) => <li key={i} className="flex items-start gap-2 text-gray-700 text-sm" data-id="element-179">
                  <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" data-id="element-180" />
                  <span data-id="element-181">{item}</span>
                </li>)}
            </ul>
          </div>

          <div className="bg-red-50 p-6 rounded-2xl border border-red-100" data-id="element-182">
            <h4 className="font-bold text-red-800 text-lg mb-4 flex items-center gap-2" data-id="element-183">
              <span className="text-xl" data-id="element-184">✖</span> Y no es para vos si:
            </h4>
            <ul className="space-y-3" data-id="element-185">
              {['Buscás que tu hijo memorice sin entender', 'Querés resultados a los gritos', 'Creés que el aprendizaje entra por presión'].map((item, i) => <li key={i} className="flex items-start gap-2 text-gray-700 text-sm" data-id="element-186">
                  <span className="text-red-500 font-bold mt-0.5 flex-shrink-0" data-id="element-187">
                    ✖
                  </span>
                  <span data-id="element-188">{item}</span>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Bonuses */}
        <BonusSection data-id="element-189" />

        {/* Final CTA / Pricing */}
        <section id="compra" className="text-center py-12 bg-white rounded-3xl shadow-xl border border-amber-100 relative overflow-hidden" data-id="element-190">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-300 via-orange-400 to-amber-300" data-id="element-191"></div>

          <div className="max-w-md mx-auto px-4 relative z-10" data-id="element-192">
            <h3 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-193">
              Empezá hoy mismo
            </h3>
            <p className="text-gray-600 mb-8" data-id="element-194">
              Acceso inmediato al libro digital + los 3 bonos de regalo
            </p>

            <div className="mb-8" data-id="element-195">
              <span className="text-gray-400 line-through text-xl mr-3" data-id="element-196">
                $47 USD
              </span>
              <span className="text-5xl font-bold text-amber-600" data-id="element-197">$9.97</span>
              <span className="text-gray-500 text-lg font-medium" data-id="element-198"> USD</span>
            </div>

            <a href="Link de tu checkout" className="block w-full bg-amber-500 hover:bg-amber-600 text-white text-xl font-bold py-5 px-8 rounded-xl shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 mb-4 animate-pulse" data-id="element-199">
              Obtener Matemática sin Lágrimas Ahora
            </a>

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6" data-id="element-200">
              <ShieldCheck className="w-4 h-4 text-green-500" data-id="element-201" />
              <span data-id="element-202">Pago 100% Seguro y Encriptado</span>
            </div>

            <div className="bg-amber-50 p-4 rounded-lg text-sm text-amber-800 border border-amber-100" data-id="element-203">
              <p className="font-bold mb-1" data-id="element-204">Garantía de Calma</p>
              <p data-id="element-205">
                Si aplicás el método y no sentís más tranquilidad en la primera
                semana, te devolvemos el dinero.
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-20 text-center text-gray-400 text-sm pb-8" data-id="element-206">
          <p data-id="element-207">
            © {new Date().getFullYear()} Matemática sin Lágrimas. Todos los
            derechos reservados.
          </p>
          <p className="mt-2" data-id="element-208">
            Este producto no garantiza resultados académicos sin la aplicación
            del método.
          </p>
        </footer>
      </main>
    </div>;
}